import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'tailwindcss/tailwind.css';
import { BASE_URL } from '../../config';
import { ArrowLeftIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon, DocumentTextIcon, DocumentIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const ViewClaimsBatch = () => {
  const { batchNo } = useParams();
  const navigate = useNavigate();
  const [batchData, setBatchData] = useState(null);
  const [claims, setClaims] = useState([]);
  const [soaFiles, setSoaFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [staleFilter, setStaleFilter] = useState('All');
  const [selectedClaim, setSelectedClaim] = useState(null);

  useEffect(() => {
    const fetchBatchDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}/claims/batches/${batchNo}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        });
        const data = response.data.data;
        console.log('Claims:', data.claims); // Debug log
        setBatchData(data);
        setClaims(data.claims);
        setSoaFiles(JSON.parse(data.soa_fileupload) || []);
      } catch (error) {
        toast.error('Failed to fetch batch details.', { autoClose: 3000 });
        setTimeout(() => navigate('/claims/batches'), 2000);
      } finally {
        setLoading(false);
      }
    };

    fetchBatchDetails();
  }, [batchNo, navigate]);

  // Filter and search logic
  const filteredClaims = useMemo(() => {
    const safeSearch = (searchQuery || '').toLowerCase();
    return claims.filter((claim) => {
      const patientName = (claim?.patient_name || '').toLowerCase();
      const transactionNo = (claim?.transaction_no || '').toLowerCase();
      const matchesSearch =
        patientName.includes(safeSearch) || transactionNo.includes(safeSearch);
      const matchesStatus =
        statusFilter === 'All' || claim?.claim_status === statusFilter;
      const isStale = Boolean(claim?.stale);
      const matchesStale =
        staleFilter === 'All' || (staleFilter === 'Yes' ? isStale : !isStale);
      return matchesSearch && matchesStatus && matchesStale;
    });
  }, [claims, searchQuery, statusFilter, staleFilter]);

  // Pagination
  const indexOfLastClaim = currentPage * itemsPerPage;
  const indexOfFirstClaim = indexOfLastClaim - itemsPerPage;
  const currentClaims = filteredClaims.slice(indexOfFirstClaim, indexOfLastClaim);
  const totalPages = Math.ceil(filteredClaims.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Generate page numbers (show up to 5 pages, centered around currentPage)
  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  // Unique statuses for filter
  const statusOptions = ['All', ...new Set(claims.map((claim) => claim.claim_status).filter(Boolean))];

  // Handle modal
  const openFilesModal = (claim) => setSelectedClaim(claim);
  const closeFilesModal = () => setSelectedClaim(null);

  const handleRefreshLink = async (file, scope = 'soa') => {
    if (!file?.upload_id) {
      toast.error('Missing upload ID for refresh.', { autoClose: 3000 });
      return;
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/claims/refresh-url`,
        { upload_id: file.upload_id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );

      const refreshedUrl =
        response.data?.signed_url ||
        response.data?.file_url ||
        response.data?.file?.signed_url;

      if (!refreshedUrl) {
        toast.error('Failed to refresh file link.', { autoClose: 3000 });
        return;
      }

      if (scope === 'soa') {
        setSoaFiles((prev) =>
          prev.map((item) =>
            item.upload_id === file.upload_id ? { ...item, signed_url: refreshedUrl } : item
          )
        );
      } else {
        setClaims((prevClaims) =>
          prevClaims.map((claim) => ({
            ...claim,
            attachedFiles: (claim.attachedFiles || []).map((item) =>
              item.upload_id === file.upload_id ? { ...item, signed_url: refreshedUrl } : item
            ),
          }))
        );
        setSelectedClaim((prev) =>
          prev
            ? {
                ...prev,
                attachedFiles: (prev.attachedFiles || []).map((item) =>
                  item.upload_id === file.upload_id ? { ...item, signed_url: refreshedUrl } : item
                ),
              }
            : prev
        );
      }

      toast.success('Link refreshed successfully.', { autoClose: 3000 });
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to refresh file link.';
      toast.error(message, { autoClose: 3000 });
    }
  };

  // Handle click outside modal
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeFilesModal();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
      </div>
    );
  }

  if (!batchData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:px-3 lg:px-4">
      <ToastContainer position="top-right" autoClose={3000} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-1xl mx-auto"
      >
        {/* Go Back Button */}
        <div className="mb-3">
          <button
            onClick={() => navigate('/claims/batches')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Go Back</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column: Batch Header and SOA Files */}
          <div className="lg:w-1/3 flex flex-col gap-8">
            {/* Batch Header */}
            <div className="bg-white shadow-sm border border-gray-100 rounded-xl p-6">
              <h1 className="text-2xl font-semibold text-gray-900 mb-4">
                Batch: {batchData.batch_no}
              </h1>
              <div className="space-y-3">
                <p className="text-gray-600">
                  <span className="font-medium">Provider:</span> {batchData.provider_name}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Received Date:</span>{' '}
                  {batchData.received_date
                    ? new Date(batchData.received_date).toLocaleDateString()
                    : 'N/A'}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Due Date:</span>{' '}
                  {batchData.due_date
                    ? new Date(batchData.due_date).toLocaleDateString()
                    : 'N/A'}
                </p>
                <p className="text-gray-600">
                  {/* <span className="font-medium">Status:</span> {batchData.batch_status} */}
                  <span className="font-medium">Status:</span>{' '}<span class="px-2 py-1 rounded text-xs bg-red-100 text-red-800">Close</span>
                </p>

                {/* <p className="text-gray-600">
                  <span className="font-medium">Estimated Claims:</span>{' '}
                  {batchData.est_claim_count || 'N/A'}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Estimated Amount:</span>{' '}
                  {batchData.est_claim_amount
                    ? `₱${parseFloat(batchData.est_claim_amount).toLocaleString()}`
                    : 'N/A'}
                </p>
                 */}

                <p className="text-gray-600">
                  <span className="font-medium">Actual Claims:</span>{' '}
                  {batchData.act_claim_count || 'N/A'}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Actual Amount:</span>{' '}
                  {batchData.act_claim_amount
                    ? `₱${parseFloat(batchData.act_claim_amount).toLocaleString()}`
                    : 'N/A'}
                </p>
                {batchData.remarks && (
                  <p className="text-gray-600">
                    {/* <span className="font-medium">Remarks:</span> {batchData.remarks} */}
                    <div class="border-l-4 border-blue-500 bg-blue-50 p-4">
                                  <h4 class="text-blue-800 font-bold">Remarks:</h4>
                                  <p class="text-blue-700">{batchData.remarks}</p>
                                  <p class="text-blue-700"></p>
                              </div>
                  </p>
                )}
              </div>
            </div>

            {/* SOA Files */}
            <div className="bg-white shadow-sm border border-gray-100 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">SOA Files</h2>
              {soaFiles.length > 0 ? (
                <ul className="space-y-2">
                  {soaFiles.map((file) => (
                    <motion.li
                      key={file.upload_id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition"
                    >
                      <a
                        href={file.signed_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {file.original_file_name}
                      </a>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500">
                          {(file.file_size / 1024).toFixed(2)} KB
                        </span>
                        <button
                          onClick={() => handleRefreshLink(file, 'soa')}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                          Refresh Link
                        </button>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500">No SOA files attached.</p>
              )}
            </div>
          </div>

          {/* Right Column: Claims Table */}
          <div className="lg:w-2/3">
            <div className="bg-white shadow-sm border border-gray-100 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Added/Attached Claims</h2>

              {/* Filters and Search */}
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Search by patient name or transaction no..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      Status: {status}
                    </option>
                  ))}
                </select>
                <select
                  value={staleFilter}
                  onChange={(e) => {
                    setStaleFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="All">Stale: All</option>
                  <option value="Yes">Stale: Yes</option>
                  <option value="No">Stale: No</option>
                </select>
              </div>

              {/* Claims Table */}
              {filteredClaims.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction No</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">LOA No</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coverage</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stale</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {currentClaims.map((claim) => (
                          <motion.tr
                            key={claim.transaction_no}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              <div className="flex flex-col gap-2">
                                <button
                                  onClick={() => openFilesModal(claim)}
                                  className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  <DocumentTextIcon className="h-5 w-5" />
                                  <span>View Attach Files</span>
                                </button>
                                {claim.loa_document && (
                                  <a
                                    href={`https://allaccess.net.ph/upload_doc/loa/${claim.loa_document}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
                                  >
                                    <DocumentIcon className="h-5 w-5" />
                                    <span>View LOA Document</span>
                                  </a>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{claim.patient_name}</td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{claim.transaction_no}</td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                              {claim.amount ? `₱${parseFloat(claim.amount).toLocaleString()}` : 'N/A'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{claim.loa_no || 'N/A'}</td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{claim.coverage || 'N/A'}</td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                              {claim.date ? new Date(claim.date).toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{claim.claim_status || 'N/A'}</td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{claim.stale ? 'Yes' : 'No'}</td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Entries Info and Pagination */}
                  <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="text-sm text-gray-600">
                      Showing {indexOfFirstClaim + 1} to{' '}
                      {Math.min(indexOfLastClaim, filteredClaims.length)} of{' '}
                      {filteredClaims.length.toLocaleString()} entries
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => paginate(1)}
                        disabled={currentPage === 1}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-md ${
                          currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <ChevronDoubleLeftIcon className="h-5 w-5" />
                        <span>First</span>
                      </button>
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-md ${
                          currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                        <span>Previous</span>
                      </button>
                      {getPageNumbers().map((page) => (
                        <button
                          key={page}
                          onClick={() => paginate(page)}
                          className={`px-3 py-1 rounded-md ${
                            currentPage === page
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-md ${
                          currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span>Next</span>
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => paginate(totalPages)}
                        disabled={currentPage === totalPages}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-md ${
                          currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span>Last</span>
                        <ChevronDoubleRightIcon className="h-5 w-5" />
                      </button>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {[10, 20, 25, 50].map((size) => (
                          <option key={size} value={size}>
                            {size} per page
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-gray-500">No claims match the current filters.</p>
              )}
            </div>
          </div>
        </div>

        {/* Attached Files Modal */}
        {selectedClaim && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={handleOverlayClick}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] flex flex-col"
            >
              <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-gray-200 rounded-t-xl">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Attached Files for Transaction {selectedClaim.transaction_no}{' '}
                    (LOA: {selectedClaim.loa_no || 'N/A'})
                  </h3>
                  <button onClick={closeFilesModal}>
                    <XMarkIcon className="h-6 w-6 text-gray-500 hover:text-gray-700" />
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 overflow-y-auto">
                {selectedClaim.attachedFiles.filter((file) => file.active).length > 0 ? (
                  <ul className="space-y-2">
                    {selectedClaim.attachedFiles
                      .filter((file) => file.active)
                      .map((file) => (
                        <li
                          key={file.upload_id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition"
                        >
                          <a
                            href={file.signed_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {file.original_file_name}
                          </a>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-500">
                              {(file.file_size / 1024).toFixed(2)} KB
                            </span>
                            <button
                              onClick={() => handleRefreshLink(file, 'claim')}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                            >
                              <ArrowPathIcon className="h-4 w-4" />
                              Refresh Link
                            </button>
                          </div>
                        </li>
                      ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">No active attached files.</p>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ViewClaimsBatch;
